import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { CommonModule } from '@angular/common';
import { NotificationsComponent } from '../notifications/notifications.component';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import {
  ActivatedRoute,
  RouterModule,
  Router,
  NavigationEnd,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslocoModule, TranslocoPipe } from '@jsverse/transloco';
import { IconsService, LanguagePickerComponent } from '@rieaya-core';
import { RouterLinkFactory } from '../../../../core/router/router-link-factory';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService } from '../../../services/permission.service';
import { PERMISSIONS_KEYS } from '../../../../core/utils/permissions';
import { LANG } from '../../../../core/utils/constant';

@Component({
  selector: 'app-header',
  imports: [
    ButtonModule,
    UserAvatarComponent,
    CommonModule,
    NotificationsComponent,
    BreadcrumbModule,
    RouterModule,
    LanguagePickerComponent,
    TranslocoModule,
    TranslocoPipe,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit {
  icons = inject(IconsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private routerLinks = inject(RouterLinkFactory);
  private destroyRef = inject(DestroyRef);
  private permissionService = inject(PermissionService);

  @Input() isMenuOpen = false;

  @Output() sidebarToggled = new EventEmitter<void>(); // Event to notify parent

  items: MenuItem[] = [];
  home: MenuItem = {
    label: 'shared/home',
    icon: this.icons.home,
    routerLink: this.routerLinks.homepage()[0],
  };

  canViewHome = false;

  LANG = LANG;

  toggleSidebar() {
    this.sidebarToggled.emit();
  }

  ngOnInit() {
    this.canViewHome = this.permissionService.hasAccess(
      PERMISSIONS_KEYS.access_homepage
    );

    this.updateBreadcrumbs();

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.updateBreadcrumbs();
      });
  }

  private updateBreadcrumbs() {
    this.items = this.buildBreadcrumbs(this.activatedRoute.root);
  }

  /** Collect params from this route and all ancestors so breadcrumb links can resolve e.g. :storeId. */
  private getParamsFromRouteAndAncestors(
    route: ActivatedRoute
  ): Record<string, string> {
    const params: Record<string, string> = {};
    let current: ActivatedRoute | null = route;
    while (current) {
      Object.keys(current.snapshot.params).forEach((key) => {
        const val = current!.snapshot.params[key];
        if (val != null) {
          params[key] = String(val);
        }
      });
      current = current.parent;
    }
    return params;
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    breadcrumbs: MenuItem[] = []
  ): MenuItem[] {
    const children: ActivatedRoute[] = route.children;
    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const breadcrumbData = child.snapshot.data['breadcrumbs'];

      if (breadcrumbData) {
        const filteredBreadcrumbs = breadcrumbData.filter((bc: any) => {
          if (!bc.permissionId) {
            return true;
          }
          return this.permissionService.hasAccess(bc.permissionId);
        });

        const params = this.getParamsFromRouteAndAncestors(child);
        breadcrumbs = filteredBreadcrumbs.map((bc: any) => {
          let parsedLink = bc.link;

          Object.keys(params).forEach((paramKey) => {
            const paramValue = params[paramKey];
            parsedLink = parsedLink.replace(`:${paramKey}`, paramValue);
          });

          if (!parsedLink.startsWith('/')) {
            parsedLink = '/' + parsedLink;
          }

          return {
            label: bc.label,
            routerLink: parsedLink.split('/').filter((p: any) => p !== ''),
          };
        });
      }

      return this.buildBreadcrumbs(child, breadcrumbs);
    }

    return breadcrumbs;
  }

  formatRouterLink(link: string[] | undefined): string[] {
    if (!link) return this.routerLinks.homepage();
    return link;
  }
}
